import type { ChatSuggestionSurface } from '@server/analytics-events';
import { Save, TriangleAlert } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import { ChartBlock } from '@/_shared/components/ChartBlock';
import { CodeBlock } from '@/_shared/components/CodeBlock';
import { RudderstackBrandLogo } from '@/_shared/components/RudderstackBrandLogo';
import { StreamingText } from '@/_shared/components/StreamingText';
import { Table } from '@/_shared/components/Table';
import { Badge } from '@/_shared/components/ui/badge';
import { Callout } from '@/_shared/components/ui/callout';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';
import { deriveColumns, parseRows, parseTableMeta } from '@/_shared/lib/table';
import { cn } from '@/_shared/lib/utils';
import type { ApprovalPayload, QuestionBlockPayload, SSEMessage } from '@/_shared/types';

import type { ChatFeedbackRating } from '../hooks/useChatFeedback';
import { useStickyArtifactKey } from '../hooks/useStickyArtifactKey';
import {
  type ChatArtifact,
  chatArtifactPinInputs,
  chatArtifactSourceId,
  toAudienceChatArtifact,
} from '../lib/chat-artifacts';
import { buildDashboardSavedSlugsPrefix } from '../lib/dashboard-activity';
import type { DashboardArtifactOpenHint } from '../lib/dashboard-draft';
import { markPanelMentions, type PanelMention } from '../lib/panel-mentions';
import type { ResolvedDashboardArtifact } from '../lib/resolved-dashboard-artifact';
import { turnIdAt } from '../lib/suggestion-analytics';
import { ActivityGroup } from './ActivityGroup';
import { AlertBlock } from './AlertBlock';
import { ChatAudienceArtifact } from './artifacts/ChatAudienceArtifact';
import { PinnedArtifactCard } from './artifacts/PinnedArtifactCard';
import { NextStepsBlock } from './assist/NextStepsBlock';
import { ChatFeedback } from './ChatFeedback';
import { ApprovalResultBanner, QuestionAnswerSummary } from './interactive/summaries';
import { getVisibleRecommendedActions, RecommendedActions } from './RecommendedActions';
import { SkillInvokedChip } from './SkillInvokedChip';
import { StickyArtifactSlot } from './StickyArtifactSlot';
import { type GroupedMessage, groupMessages, isToolActive } from './tool-call-meta';
import { WorkflowPreviewBlock } from './WorkflowPreviewBlock';

export interface MessageListFeedback {
  canRate: boolean;
  eligibleTurnIds: Set<string>;
  ratingByTurn: Map<string, ChatFeedbackRating | null>;
  isPending: boolean;
  onRate: (turnId: string, rating: ChatFeedbackRating | null) => void;
}

interface MessageListProps {
  messages: SSEMessage[];
  thinking?: boolean;
  onAction?: (message: string) => void;
  /** Compact mode for sidebar — reduced padding, no max-width constraint. */
  compact?: boolean;
  /** When false, recommend_actions blocks are hidden (e.g. until the turn closes). Defaults to true. */
  showRecommendActions?: boolean;
  /** Disables recommend_actions buttons (e.g. while a turn is active). */
  actionsDisabled?: boolean;
  /** When true, save-dashboard suggestions are stripped from recommend_actions. */
  dashboardSaved?: boolean;
  /** Which chat surface hosts this list, for suggestion analytics. Omitted, suggestions are not tracked. */
  suggestionSurface?: ChatSuggestionSurface;
  /** The rETL connection the chat is about (the Ask why chat): its NEXT STEPS events carry it. */
  suggestionConnectionId?: string;
  onDashboardArtifactOpen?: (slug: string, hint?: DashboardArtifactOpenHint) => void;
  resolveDashboardArtifact?: (slug: string) => ResolvedDashboardArtifact | null;
  /** Pre-grouped messages from a host that already derived them; falls back to grouping here. */
  grouped?: GroupedMessage[];
  savedSlugsPrefix?: Set<string>[];
  /**
   * The transcript's artifacts, derived by the host (which also owns the live dashboard store). Omit in
   * surfaces that produce none — there is then nothing to pin.
   */
  artifacts?: ChatArtifact[];
  /** Thumbs feedback for eligible completed turns. Omit to hide controls. */
  feedback?: MessageListFeedback;
  /** Host content rendered under each finished answer (after its `turn_complete`), above its feedback. */
  turnFooter?: (turnId: string) => React.ReactNode;
}

/**
 * A message the user sent.
 *
 * `referencedPanels` marks up the panels this message actually referenced, from its own stored record.
 * Reading the live dashboard instead would re-interpret history: renaming a panel would un-highlight
 * every past mention of it, and adding a panel would highlight a message as naming something that did
 * not exist when the agent answered. A message with no stored record predates the field, so it renders
 * as the plain prose it always was.
 */
function UserBubble({
  content,
  referencedPanels,
  stopped = false,
}: {
  content: string;
  referencedPanels?: PanelMention[];
  /** The turn this message started was stopped before it finished. */
  stopped?: boolean;
}): React.ReactElement {
  return (
    <div className={cn('flex justify-end px-6 py-3', stopped && 'flex-col items-end gap-1')}>
      <div
        className="max-w-[85%] animate-slide-up rounded-2xl border border-border bg-card px-4 py-2.5 text-sm"
        data-testid="user-bubble"
      >
        <StreamingText content={referencedPanels?.length ? markPanelMentions(content, referencedPanels) : content} />
      </div>
      {stopped && <span className="text-xs text-muted-foreground">Stopped</span>}
    </div>
  );
}

const NO_ARTIFACTS: ChatArtifact[] = [];

type AssistGroup = Extract<GroupedMessage, { kind: 'single' }> & {
  message: Extract<SSEMessage, { type: 'ui_message' }>;
};

function isAssistGroup(group: GroupedMessage): group is AssistGroup {
  return group.kind === 'single' && group.message.type === 'ui_message' && group.message.uiType === 'assist_suggestion';
}
// Shared so an absent prefix entry does not mint a new Set per render and defeat ActivityGroup's memo.
const NO_SAVED_SLUGS = new Set<string>();

function renderSSEMessage(
  message: SSEMessage,
  index: number,
  blockPayloads?: Map<string, QuestionBlockPayload | ApprovalPayload>,
  /** Key of the artifact currently pinned to the top — its inline card yields to that copy. */
  stickyArtifactKey?: string | null,
  stickySources?: ReadonlyMap<string, string>,
): React.ReactElement | null {
  switch (message.type) {
    case 'user_message':
      return (
        <UserBubble
          key={`user-${index}`}
          content={message.content}
          {...(message.referencedPanels ? { referencedPanels: message.referencedPanels } : {})}
        />
      );

    case 'assistant':
      // Transient — latest assistant text surfaces via ThinkingIndicator shimmer.
      // The last assistant text before each tool group is captured as group context.
      return null;

    case 'ui_message': {
      if (message.uiType === 'change_title') return null;
      if (message.uiType === 'recommend_actions') return null;
      if (message.uiType === 'workflow_preview') {
        return (
          <div key={`ui-${index}`} className="mx-6 my-2 animate-slide-up">
            <WorkflowPreviewBlock content={message.content} />
          </div>
        );
      }
      // 'measurement_plan' is gone from SEND_TO_UI_TYPES, so it is absent from the uiType union and
      // needs a cast — historical chat rows still carry it, and without this guard they fall through
      // to the generic default and render their raw JSON payload as a paragraph.
      if ((message.uiType as string) === 'measurement_plan') {
        return (
          <div key={`ui-${index}`} className="mx-6 my-2 animate-slide-up">
            <Callout variant="info">This card type is no longer supported.</Callout>
          </div>
        );
      }
      // dashboard_artifact ui_messages are sidecar records for the tool-activity cards (rendered by
      // ActivityGroup from the tool_use/tool_result pair) — groupMessages skips them, so they never
      // reach here. The card renders through one path only: the tool activity.

      // Assist cards are drawn by the transcript loop, as rows of the turn's NextStepsBlock.
      if (message.uiType === 'assist_suggestion') return null;

      // audience_artifact ui_messages are emitted directly on the generic ui_message bus by the
      // save_audience_draft MCP tool (not the sidecar path), so they DO reach here and render inline.
      // The key attribute is what useStickyArtifactKey observes to know when this card has scrolled past
      // and its compact copy should pin to the top instead.
      if (message.uiType === 'audience_artifact') {
        const artifact = toAudienceChatArtifact(message.content, message.metadata, index);
        return (
          <StickyArtifactSlot
            key={`ui-${index}`}
            artifactKey={artifact.key}
            sourceId={chatArtifactSourceId(index, 0)}
            stickySources={stickySources}
            stickyArtifactKey={stickyArtifactKey}
            className="mx-6 my-2 animate-slide-up"
          >
            <ChatAudienceArtifact artifact={artifact} />
          </StickyArtifactSlot>
        );
      }

      const inner = (() => {
        switch (message.uiType) {
          case 'table':
            return <TableMessage content={message.content} metadata={message.metadata} />;
          case 'code':
            return <CodeBlock content={message.content} metadata={message.metadata} />;
          case 'alert':
            return <AlertBlock content={message.content} metadata={message.metadata} />;
          case 'chart':
            return <ChartBlock content={message.content} metadata={message.metadata} />;
          case 'text':
          case 'dashboard_artifact':
          default:
            return <StreamingText content={message.content} />;
        }
      })();
      return (
        <div key={`ui-${index}`} className="mx-6 my-2 min-w-0 animate-slide-up">
          {inner}
        </div>
      );
    }

    case 'turn_aborted':
      // A stopped turn leaves the transcript, and its marker shows the prompt the user stopped, in
      // every tab and after a reload. The composer is never touched, so a draft stays where it is.
      // A marker with no prompt (a swept orphan) shows nothing.
      if (!message.userMessage) return null;
      return (
        <UserBubble
          key={`stopped-${index}`}
          content={message.userMessage}
          stopped
          {...(message.referencedPanels ? { referencedPanels: message.referencedPanels } : {})}
        />
      );

    case 'result':
    case 'turn_start':
      return null;

    case 'turn_complete':
      if (message.outcome === 'interrupted') {
        return (
          <div key={`interrupted-${index}`} className="mx-6 my-2 animate-slide-up">
            <Callout variant="warning">This response was interrupted. Send your message again to continue.</Callout>
          </div>
        );
      }
      return null;

    case 'error':
      return (
        <div
          key={`error-${index}`}
          className="mx-6 my-2 flex animate-slide-up items-start gap-2 rounded-lg border border-danger-border/50 bg-danger-bg p-3 text-sm text-danger"
        >
          <TriangleAlert size={16} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p>{message.message}</p>
            {message.code === 'HISTORY_UNAVAILABLE' && <HistoryUnavailableAction />}
          </div>
        </div>
      );

    case 'tool_use':
    case 'tool_result':
      return null;

    case 'skill_invoked':
      return <SkillInvokedChip key={`skill-invoked-${index}`} message={message} />;

    case 'memory_flushed':
      return (
        <div key={`memory-flushed-${index}`} className="mx-6 my-1" data-testid="memory-flushed-chip">
          <Badge variant="success" bordered className="gap-1 text-xs">
            <Save className="size-3" />
            Saved {message.count} {message.count === 1 ? 'fact' : 'facts'} before summarizing
          </Badge>
        </div>
      );

    case 'compaction_started':
      return (
        <div key={`compact-start-${index}`} className="mx-6 my-1" data-testid="compaction-started-chip">
          <span className="text-xs text-muted-foreground italic">Summarizing earlier turns...</span>
        </div>
      );
    case 'awaiting_input':
      // The pending block takes over the composer (see ChatConversation), not the transcript.
      return null;

    case 'interactive_answer':
      return message.answer.kind === 'approval' ? (
        <ApprovalResultBanner
          key={`ia-${index}`}
          answer={message.answer}
          payload={blockPayloads?.get(message.blockId) as ApprovalPayload | undefined}
        />
      ) : (
        <QuestionAnswerSummary
          key={`ia-${index}`}
          answer={message.answer}
          payload={blockPayloads?.get(message.blockId) as QuestionBlockPayload | undefined}
        />
      );

    default:
      // Exhaustiveness guard: every SSEMessage type must have an explicit case above. A new type
      // narrows `message` to `never` here and fails to compile ("not assignable to never"), so an
      // unhandled event can never silently fall through to nothing (or render raw content).
      return assertNever(message);
  }
}

function HistoryUnavailableAction(): React.ReactElement | null {
  const workspace = useWorkspaceOrNull();
  if (!workspace) return null;
  return (
    <Link to={workspace.wp()} className="mt-2 inline-block font-medium underline underline-offset-2">
      Start a new chat
    </Link>
  );
}

/** Compile-time exhaustiveness check: unreachable at runtime if every union member is handled. */
function assertNever(message: never): never {
  throw new Error(`Unhandled SSE message type: ${JSON.stringify(message)}`);
}

function TableMessage({ content, metadata }: { content: string; metadata?: unknown }): React.ReactElement {
  const rows = useMemo(() => parseRows(content), [content]);
  const columns = useMemo(() => deriveColumns(rows), [rows]);
  if (rows.length === 0) {
    return <pre className="overflow-x-auto text-xs text-muted-foreground">{content}</pre>;
  }
  const meta = parseTableMeta(metadata);
  return (
    <Table
      data={rows}
      columns={columns}
      title={meta.title}
      totalRows={meta.totalRows}
      enableCsvExport
      enableSort
      showRowCount
    />
  );
}

function truncateThinking(text: string, max = 200): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}

function ThinkingIndicator({ text }: { text: string }): React.ReactElement {
  return (
    <div className="mx-6 mt-3 mb-1 flex animate-slide-up items-start gap-2" data-testid="thinking-indicator">
      <RudderstackBrandLogo variant="compact" className="mt-1 size-3" />
      <span className="animate-shimmer text-sm text-muted-foreground" role="status">
        {truncateThinking(text)}
      </span>
    </div>
  );
}

export function MessageList({
  messages,
  thinking,
  onAction,
  compact = false,
  showRecommendActions = true,
  actionsDisabled = false,
  dashboardSaved = false,
  suggestionSurface,
  suggestionConnectionId,
  onDashboardArtifactOpen,
  resolveDashboardArtifact,
  grouped: groupedFromHost,
  savedSlugsPrefix: savedSlugsPrefixFromHost,
  artifacts = NO_ARTIFACTS,
  feedback,
  turnFooter,
}: MessageListProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const userMessageCountRef = useRef(0);
  const prevScrollHeightRef = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const userMessageCount = messages.reduce((n, m) => (m.type === 'user_message' ? n + 1 : n), 0);
    const userJustSent = userMessageCount - userMessageCountRef.current === 1;
    // 0 → N transition on user_message count: history just loaded (initial
    // mount or session switch after clearMessages). Every session has at least
    // one user_message, so this reliably detects the bulk load and lets us
    // jump instantly without animation.
    const bulkLoad = userMessageCountRef.current === 0 && userMessageCount > 0;
    userMessageCountRef.current = userMessageCount;

    // Check against the *previous* scrollHeight, not the current one — a large
    // incoming message can push the bottom hundreds of px in a single render,
    // and a fixed "distance from current bottom" threshold would treat a
    // still-pinned user as scrolled away.
    const prevScrollHeight = prevScrollHeightRef.current;
    const wasAtBottom = el.scrollTop + el.clientHeight >= prevScrollHeight - 50;
    prevScrollHeightRef.current = el.scrollHeight;

    if (bulkLoad) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'instant' });
    } else if (userJustSent || wasAtBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const lastAssistantText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg?.type === 'assistant') return msg.content;
      if (msg?.type === 'ui_message' || msg?.type === 'error' || msg?.type === 'user_message') break;
    }
    return null;
  }, [messages]);

  // The host derives these when it has them (it owns the live dashboard store too); this fallback keeps
  // the component self-sufficient for surfaces that pass only `messages`.
  const groupedLocal = useMemo(() => groupMessages(messages), [messages]);
  const savedSlugsLocal = useMemo(() => buildDashboardSavedSlugsPrefix(messages), [messages]);
  const grouped = groupedFromHost ?? groupedLocal;
  const savedSlugsPrefix = savedSlugsPrefixFromHost ?? savedSlugsLocal;

  // The artifact pinned over the top of the transcript — the last one (of any type) whose inline card has
  // scrolled past, so its CTA stays reachable however far the conversation runs on.
  const pin = useMemo(() => chatArtifactPinInputs(artifacts), [artifacts]);
  const stickyKey = useStickyArtifactKey(containerRef, pin.keys, pin.epoch);
  const stickyArtifact = (stickyKey === null ? undefined : pin.byKey.get(stickyKey)) ?? null;

  // blockId → payload from awaiting_input, so a resolved interactive_answer summary can show the prompt.
  const blockPayloads = useMemo(() => {
    const map = new Map<string, QuestionBlockPayload | ApprovalPayload>();
    for (const m of messages) {
      if (m.type === 'awaiting_input') map.set(m.blockId, m.payload);
    }
    return map;
  }, [messages]);
  const toolActive = isToolActive(messages);

  const thinkingText = thinking ? truncateThinking(lastAssistantText ?? 'Thinking…') : undefined;

  // Find the index of the last recommend_actions block — only that one should
  // be rendered, and only if no subsequent turn has started after it.
  let lastRecommendIdx = -1;
  let hasUserMessageAfterLastRecommend = false;
  for (let i = grouped.length - 1; i >= 0; i--) {
    const g = grouped[i]!;
    if (g.kind === 'single' && g.message.type === 'ui_message' && g.message.uiType === 'recommend_actions') {
      lastRecommendIdx = i;
      break;
    }
    // If there's a user_message after the last recommend_actions, a new turn started. A stopped prompt
    // shown in the transcript started one too, so the earlier suggestions stay hidden.
    if (
      g.kind === 'single' &&
      (g.message.type === 'user_message' || (g.message.type === 'turn_aborted' && !!g.message.userMessage))
    ) {
      hasUserMessageAfterLastRecommend = true;
    }
  }

  // Only show recommend_actions for the last completed turn — hide if:
  // - showRecommendActions is false
  // - this isn't the last recommend_actions block
  // - a new turn started after this block (user sent another message)
  // - agent is currently active (thinking)
  const recommendShown = (at: number, content: string): boolean =>
    showRecommendActions &&
    at === lastRecommendIdx &&
    !hasUserMessageAfterLastRecommend &&
    !thinking &&
    getVisibleRecommendedActions(content, dashboardSaved).length > 0;

  const renderItems: React.ReactElement[] = [];
  for (let i = 0; i < grouped.length; i++) {
    const group = grouped[i]!;
    const isLastGroup = i === grouped.length - 1;
    const isActiveGroup = isLastGroup && toolActive;

    // A turn's assist cards (groupMessages puts them together, just above the follow-up chips) render as
    // the top rows of one Next steps block with those chips, or alone in the same frame while the chips
    // are hidden. Keyed by the first card's stored seq, not its transcript index, so the cards keep their
    // state as the chips come and go and when a late card from an earlier turn is inserted above them.
    if (isAssistGroup(group)) {
      const cards: { id: string; content: string }[] = [];
      let next = i;
      for (let g = grouped[next]; g && isAssistGroup(g); g = grouped[++next]) {
        cards.push({
          id: g.message.seq != null ? `seq-${g.message.seq}` : `index-${g.index}`,
          content: g.message.content,
        });
      }
      const after = grouped[next];
      const chips =
        after?.kind === 'single' && after.message.type === 'ui_message' && after.message.uiType === 'recommend_actions'
          ? { index: after.index, message: after.message }
          : null;
      const shown = chips !== null && recommendShown(next, chips.message.content);
      renderItems.push(
        <NextStepsBlock
          key={`next-steps-${cards[0]!.id}`}
          cards={cards}
          {...(chips && shown
            ? {
                chips: {
                  content: chips.message.content,
                  metadata: chips.message.metadata,
                  onAction,
                  disabled: actionsDisabled,
                  dashboardSaved,
                  surface: suggestionSurface,
                  turnId: turnIdAt(messages, chips.index),
                  rsConnectionId: suggestionConnectionId,
                },
              }
            : {})}
        />,
      );
      // The chips' own group is drawn in the block (or hidden with it); the loop resumes after it.
      i = chips ? next : next - 1;
      continue;
    }

    if (group.kind === 'activity') {
      renderItems.push(
        <ActivityGroup
          key={`activity-${group.startIndex}`}
          items={group.items}
          isActive={isActiveGroup}
          context={group.context}
          savedSlugsBefore={savedSlugsPrefix[group.startIndex] ?? NO_SAVED_SLUGS}
          onDashboardArtifactOpen={onDashboardArtifactOpen}
          resolveDashboardArtifact={resolveDashboardArtifact}
          stickyArtifactKey={stickyKey}
          startIndex={group.startIndex}
          stickySources={pin.sources}
        />,
      );
    } else if (group.message.type === 'ui_message' && group.message.uiType === 'recommend_actions') {
      if (!recommendShown(i, group.message.content)) continue;
      renderItems.push(
        <div key={`actions-${group.index}`} className="mx-6 my-2 animate-slide-up">
          <RecommendedActions
            content={group.message.content}
            metadata={group.message.metadata}
            onAction={onAction}
            disabled={actionsDisabled}
            dashboardSaved={dashboardSaved}
            surface={suggestionSurface}
            turnId={turnIdAt(messages, group.index)}
            rsConnectionId={suggestionConnectionId}
          />
        </div>,
      );
    } else {
      const el = renderSSEMessage(group.message, group.index, blockPayloads, stickyKey, pin.sources);
      if (el) renderItems.push(el);
      if (turnFooter && group.message.type === 'turn_complete') {
        const footer = turnFooter(group.message.turnId);
        if (footer) renderItems.push(<Fragment key={`footer-${group.message.turnId}`}>{footer}</Fragment>);
      }
      // One control pair at the end of each eligible completed answer (after turn_complete).
      if (
        feedback &&
        group.message.type === 'turn_complete' &&
        group.message.outcome !== 'interrupted' &&
        feedback.eligibleTurnIds.has(group.message.turnId)
      ) {
        const turnId = group.message.turnId;
        renderItems.push(
          <ChatFeedback
            key={`feedback-${turnId}`}
            turnId={turnId}
            rating={feedback.ratingByTurn.get(turnId) ?? null}
            canRate={feedback.canRate}
            busy={feedback.isPending}
            onRate={(rating) => feedback.onRate(turnId, rating)}
          />,
        );
      }
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={containerRef} className="min-h-0 flex-1 overflow-y-auto" role="log" aria-live="polite">
        {/* Keep max-w-4xl in compact (side-panel) mode so transcript matches the composer width. */}
        <div className={cn('mx-auto max-w-4xl', compact ? 'py-3' : 'py-6')}>
          {/* Compact copy of the scrolled-past artifact. Lives IN this column (not an overlay on the
              pane) so it shares the scroller's client width — a sibling overlay was centered in the
              padding box, half a scrollbar too far right of the messages. `h-0` keeps it out of flow
              (the inline card stays `invisible` and holds the gap); sticky is only how this copy
              stays on screen. `pointer-events-none` on the gutter keeps the transcript scrollable
              either side of the card. */}
          {stickyArtifact && (
            // `aria-live="off"` because this subtree lives inside the transcript's `role="log"` region: the
            // pin appears and swaps on SCROLL, and without this every handover would re-announce a card the
            // reader already heard when it streamed in.
            <div
              className="pointer-events-none sticky top-2 z-20 h-0 px-6"
              role="region"
              aria-label="Pinned artifact"
              aria-live="off"
            >
              <div className="pointer-events-auto">
                <PinnedArtifactCard
                  artifact={stickyArtifact}
                  {...(onDashboardArtifactOpen && { onDashboardOpen: onDashboardArtifactOpen })}
                />
              </div>
            </div>
          )}
          {renderItems.length === 0 && !compact && (
            <div className="flex h-full items-center justify-center py-32 text-center text-muted-foreground">
              <div>
                <div className="mb-2 text-lg">Rudder Lookout</div>
                <div className="mb-2 text-sm">Ask about your data pipeline, warehouse health, or PR reviews.</div>
                <span className="inline-block rounded-full border border-brand/20 bg-brand/8 px-2.5 py-0.5 text-[0.625rem] font-semibold tracking-wider text-brand uppercase">
                  Private Beta
                </span>
              </div>
            </div>
          )}
          {renderItems}
          {thinking && <ThinkingIndicator text={thinkingText ?? 'Thinking…'} />}
        </div>
      </div>
    </div>
  );
}
